import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'

import { OnboardingLayout } from '../Layout/Layout'

import { useAuth } from '@percent/workplace-giving/common/hooks'
import {
  getAccountFromAuthState,
  getPartnerFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { Button, Checkbox, FormField, TextInput, Select, Text } from '@percent/lemonade'
import { config } from '@percent/workplace-giving/config/config'
import { useProfileCreation } from './useProfileCreation'
import classes from './ProfileCreation.module.scss'
import { ProfileCreationSkeleton } from './ProfileCreationSkeleton'
import { useShouldRunOnboarding } from '@percent/workplace-giving/common/hooks/useShouldRunOnboarding/useShouldRunOnboarding'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'

export function ProfileCreation() {
  const { t } = useTranslation()
  const { state } = useAuth()
  const user = getAccountFromAuthState(state)
  const { features } = getPartnerFromAuthState(state)!
  const secondaryEmailEnabled = features?.profileSecondaryEmails === 'enabled'
  const onboardingEnabled = useShouldRunOnboarding()
  const navigate = useNavigate()

  const {
    handleSubmit,
    touched,
    errors,
    error,
    handleChange,
    isSubmitting,
    buttonDisabled,
    handleBlur,
    values,
    currencyOptions,
    setFieldValue,
    defaultCurrencyOption,
    showCurrencyOptions,
    isLoading
  } = useProfileCreation()

  useEffect(() => {
    if (!onboardingEnabled) {
      navigate(AppRoute.HOME, { replace: true })
    }
  }, [])

  if (isLoading) {
    return (
      <OnboardingLayout
        cardTitle={t('workplace_giving.onboarding.profile_creation.title')}
        cardDescription={t('workplace_giving.onboarding.profile_creation.description')}
      >
        <ProfileCreationSkeleton withPreferredNameField={!user?.preferredName} />
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      cardTitle={t('workplace_giving.onboarding.profile_creation.title')}
      cardDescription={t('workplace_giving.onboarding.profile_creation.description')}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <FormField
          label={t('workplace_giving.onboardingPopup.label.fullName')}
          status={touched.fullName && errors.fullName ? 'danger' : 'default'}
          statusMessage={errors.fullName}
          data-testid="fullName"
          necessity="required"
          description={t('workplace_giving.onboardingPopup.input.description.fullName')}
        >
          <TextInput
            name="fullName"
            value={values.fullName}
            placeholder={t('workplace_giving.onboardingPopup.placeholder.fullName')}
            onBlur={handleBlur}
            onChange={handleChange}
            readOnly={!!user?.fullName}
            borderRadiiSize="large"
          />
        </FormField>

        {!user?.preferredName ? (
          <FormField
            label={t('workplace_giving.onboardingPopup.label.preferredName')}
            status={touched.preferredName && errors.preferredName ? 'danger' : 'default'}
            statusMessage={errors.preferredName}
            data-testid="preferredName"
            necessity="required"
            description={t('workplace_giving.onboardingPopup.input.description.preferredName')}
          >
            <TextInput
              name="preferredName"
              data-testid="preferredName"
              value={values.preferredName}
              placeholder={t('workplace_giving.onboardingPopup.placeholder.preferredName')}
              onBlur={handleBlur}
              onChange={handleChange}
              borderRadiiSize="large"
            />
          </FormField>
        ) : null}

        {secondaryEmailEnabled ? (
          <FormField
            label={t('workplace_giving.onboardingPopup.label.secondaryEmail')}
            status={touched.secondaryEmail && errors.secondaryEmail ? 'danger' : 'default'}
            statusMessage={errors.secondaryEmail}
            data-testid="secondaryEmail"
            necessity="optional"
            description={t('workplace_giving.onboardingPopup.input.description.secondaryEmail')}
          >
            <TextInput
              name="secondaryEmail"
              data-testid="secondaryEmail"
              value={values.secondaryEmail}
              placeholder={t('workplace_giving.onboardingPopup.placeholder.secondaryEmail')}
              onBlur={handleBlur}
              onChange={handleChange}
              borderRadiiSize="large"
            />
          </FormField>
        ) : null}

        {error ? (
          <Text size="small" className={classes.errorMessage}>
            {error}
          </Text>
        ) : null}

        {showCurrencyOptions ? (
          <FormField
            label={t('workplace_giving.onboardingPopup.label.preferredCurrency')}
            status={touched.preferredCurrency && errors.preferredCurrency ? 'danger' : 'default'}
            statusMessage={errors.preferredCurrency}
            data-testid="preferredCurrency"
            necessity="required"
            description={t('workplace_giving.onboardingPopup.input.description.preferredCurrency')}
            disabled={currencyOptions?.length === 1}
          >
            <Select
              onChange={event => {
                setFieldValue('preferredCurrency', event.value)
              }}
              defaultValue={defaultCurrencyOption}
              options={currencyOptions!}
              data-testid="currencySelect"
              showPrefixForSelectedOption
              borderRadiiSize="large"
              placeholder=""
            />
          </FormField>
        ) : null}

        <div className={classes.spacer} />

        <Text size="small" className={classes.termsInfo}>
          {t('workplace_giving.onboardingPopup.termsAndConditionsInfo')}
        </Text>

        <Checkbox
          name="termsAndConditions"
          dataTestId="terms-checkbox"
          errorMessage={touched.termsAndConditions && errors.termsAndConditions ? errors.termsAndConditions : ''}
          active={values.termsAndConditions}
          onBlur={handleBlur}
          label={
            <Trans
              t={t}
              i18nKey="workplace_giving.onboardingPopup.termsAndConditionsCheckbox"
              components={{
                1: <Link to={config.terms['workplace:terms:2025-12-15']} target="_blank" rel="noopener noreferrer" />,
                2: <Link to={config.terms['percent:privacy:2025-12-15']} target="_blank" rel="noopener noreferrer" />
              }}
            />
          }
          necessity="required"
          variant="default"
          onChange={handleChange}
        />

        <div className={classes.buttonsWrapper}>
          <Button
            data-testid="submitButton"
            variant="primary"
            type="submit"
            shape="oval"
            loading={isSubmitting}
            disabled={buttonDisabled}
          >
            {t('workplace_giving.onboardingPopup.continueButton')}
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  )
}
